import { Clock, CheckCircle2, TrendingDown } from 'lucide-react';
import { ThreatArticle, Incident } from '../data/demoData';
import { formatTimeAgo } from '../lib/utils';
import { IocReputationCard } from './IocReputationCard';

interface ThreatIntelSectionProps {
  articles: ThreatArticle[];
  mttd: string;
  mttr: string;
  noiseRatio: string;
  incidents?: Incident[];
  customerId?: string;
}

const SEVERITY_COLORS: Record<string, { border: string; text: string }> = {
  Critical: { border: 'border-l-red-600', text: 'text-red-600' },
  High: { border: 'border-l-orange-500', text: 'text-orange-500' },
  Medium: { border: 'border-l-blue-500', text: 'text-blue-600' },
  Info: { border: 'border-l-green-500', text: 'text-green-600' },
};

export function ThreatIntelSection({ articles, mttd, mttr, noiseRatio, customerId }: ThreatIntelSectionProps) {
  return (
    <>
      {articles.length > 0 && (
        <section className="mb-6">
          <h2 className="text-white dark:text-foreground text-base font-semibold mb-4 uppercase tracking-wide">
            Threat Intelligence: Latest OSINT Reports
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
            {articles.map(article => {
              const sc = SEVERITY_COLORS[article.severity] || SEVERITY_COLORS['Info'];
              const sourceLabel = article.source || 'Kloudynet TI';
              return (
                <div key={article.id}
                  className={`bg-card rounded-xl p-5 shadow kpi-hover cursor-pointer border-l-4 ${sc.border}`}
                  onClick={() => window.open(article.url, '_blank')}
                  title={`Open ${sourceLabel} report`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className={`text-xs font-bold uppercase ${sc.text}`}>{article.severity}</span>
                    <span className="text-[10px] text-muted-foreground truncate ml-2 max-w-[60%]" title={sourceLabel}>
                      {sourceLabel}
                    </span>
                  </div>
                  <p className="text-sm font-semibold text-foreground leading-snug mb-2 line-clamp-3">{article.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatTimeAgo(article.createdDateTime)}
                    {article.tags.length > 0 && (
                      <> &middot; {article.tags.slice(0, 2).join(', ')}</>
                    )}
                  </p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-card rounded-xl p-5 shadow">
          <div className="flex justify-between items-start mb-3">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Mean Time to Detect</span>
            <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950 flex items-center justify-center">
              <Clock size={16} strokeWidth={2} className="text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="text-4xl font-bold mb-2 text-primary">{mttd}</div>
          <p className="text-xs text-muted-foreground">Avg triage time for active incidents</p>
        </div>
        <div className="bg-card rounded-xl p-5 shadow">
          <div className="flex justify-between items-start mb-3">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Mean Time to Resolve</span>
            <div className="w-8 h-8 rounded-lg bg-green-50 dark:bg-green-950 flex items-center justify-center">
              <CheckCircle2 size={16} strokeWidth={2} className="text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div className="text-4xl font-bold mb-2 text-green-600 dark:text-green-400">{mttr}</div>
          <p className="text-xs text-muted-foreground">Avg resolution time for closed incidents</p>
        </div>
        <div className="bg-card rounded-xl p-5 shadow">
          <div className="flex justify-between items-start mb-3">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Alert Noise Ratio</span>
            <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
              <TrendingDown size={16} strokeWidth={2} className="text-muted-foreground" />
            </div>
          </div>
          <div className="text-4xl font-bold mb-2 text-muted-foreground">{noiseRatio}</div>
          <p className="text-xs text-muted-foreground">FP/Benign/Undetermined ratio</p>
        </div>
      </div>

      <IocReputationCard customerId={customerId} />
    </>
  );
}
